import { useQuery, useQueryClient } from '@tanstack/react-query'

import { authAPI } from '@/api/new-api'
import { ClientTableStatus } from '@/models/features/clients'

export type ClientProfileResponse = {
  id: string
  active: boolean
  apartment: number | null
  buildingNumber: string | null
  floor: number | null
  moveInDate: number | null
  moveOutDate: number | null
  leaseStart: number | null
  leaseEnd: number | null
  pets: boolean | null
  inHouse: boolean | null | undefined
  street: string | null
  zipCode: string | null
  birth: string | null
  phone: number | string | null
  tags: string[] | null
  mailingList: string[] | null
  buildingId: {
    id: number
    code?: string
    name?: string
    timezone?: string
    amenityPassesAvailable?: boolean
    dormakabaAvailable?: boolean
    remoteLockAvailable?: boolean
    remoteLockFobAvailable?: boolean
    saltoSpaceAvailable?: boolean
    saltoSpaceFobsAvailable?: boolean
    swiftlaneAvailable?: boolean
    visionlineAvailable?: boolean
    pointCentralAvailable?: boolean
    iglooWorkAvailable?: boolean
    iglooWorkAlgoPinAvailable?: boolean
    iglooWorkCustomPinAvailable?: boolean
    // Note: gate whether each Resident Profile module tab is shown at all - see
    // client-profile.utils.ts's getModuleAvailability. Same flags the main Navbar's Can
    // component checks (src/components/Can.js's ABILITIES.Navbar feedFlag usages).
    packagesAvailable?: boolean
    checklistsAvailable?: boolean
    rewardsAvailable?: boolean
    maintenanceRequestAvailable?: boolean
    guestAccess?: boolean
    packagesD2DAvailable?: boolean
    amenityReservations?: boolean
    appUserStatusAvailable?: boolean
    keylessAccessAvailable?: boolean
    [key: string]: unknown
  }
  isKeylessAccessSuspended: boolean | null
  lastAppLogin: number | null
  statusPMS: { id: number; status: ClientTableStatus } | null
  yardiId: string | null
  entrataId: number | null
  resmanId: string | null
  realPageId: number | null
  rentManagerId: number | null
  parkingSpots: string[]
  user: {
    id: number
    email: string
    firstName: string
    isActive: boolean
    lastName: string
    avatar: string | null
  }
}

type GetClientProfileRequest = {
  clientId: string
}

export const getClientProfileQueryKey = (data: GetClientProfileRequest) => [
  'useGetClientProfile',
  data,
]

const getClientProfile = async (data: GetClientProfileRequest) => {
  const response = await authAPI.get<ClientProfileResponse>(`/client/${data.clientId}`)
  return response.data
}

export const useGetClientProfile = (data: GetClientProfileRequest) =>
  useQuery({
    queryFn: () => getClientProfile(data),
    queryKey: getClientProfileQueryKey(data),
  })

export const useInvalidateClientProfile = (clientId: string) => {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: getClientProfileQueryKey({ clientId }) })
}
